import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { v4 as uuidv4 } from 'uuid';
import firebase from '../utilities/firebase';
import { StackedDialog, useStackZIndex, useSelectMenuProps } from '../utilities/ModalStackContext';

export default function AddInvoiceDialog({ open, onClose, stockNumber, onInvoiceAdded, type = 'regular', tax_deduction = 0, editMode = false, invoiceData = null }) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [formData, setFormData] = React.useState({
    cashPrice: '',
    docFee: '',
    titleFee: '',
    tagFee: '',
    conciergeFee: '',
    downpayment: '',
    hasTax: true,
    salesTax: '',
    surtax: '',
    total: '',
    taxDeduction: tax_deduction
  });

  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  // Initialize form data based on type and edit mode
  React.useEffect(() => {
    if (editMode && invoiceData) {
      // Pre-populate form with existing invoice data
      if (type === 'finance') {
        setFormData({
          conciergeFee: invoiceData.conciergeFee || '',
          participationFee: invoiceData.participationFee || '',
          adjustments: invoiceData.adjustments || '',
          total: String(invoiceData.total || ''),
          taxDeduction: invoiceData.taxDeduction || tax_deduction
        });
      } else {
        setFormData({
          cashPrice: invoiceData.cashPrice || '',
          docFee: invoiceData.docFee || '',
          titleFee: invoiceData.titleFee || '',
          tagFee: invoiceData.tagFee || '',
          conciergeFee: invoiceData.conciergeFee || '',
          downpayment: invoiceData.downpayment || '',
          hasTax: invoiceData.hasTax !== undefined ? invoiceData.hasTax : true,
          salesTax: invoiceData.salesTax || '',
          surtax: invoiceData.surtax || '',
          total: String(invoiceData.total || ''),
          taxDeduction: invoiceData.taxDeduction || tax_deduction
        });
      }
    } else if (type === 'finance') {
      setFormData({
        conciergeFee: '',
        participationFee: '',
        adjustments: '',
        total: '',
        taxDeduction: tax_deduction
      });
    } else {
      setFormData({
        cashPrice: '',
        docFee: '',
        titleFee: '',
        tagFee: '',
        conciergeFee: '',
        downpayment: '',
        hasTax: true,
        salesTax: '',
        surtax: '',
        total: '',
        taxDeduction: tax_deduction
      });
    }
  }, [type, tax_deduction, editMode, invoiceData]);

  const handleInputChange = (field, value) => {
    if (loading) return; // Prevent changes while loading
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const calculateTotal = () => {
    if (type === 'finance') {
      const conciergeFee = parseFloat(formData.conciergeFee) || 0;
      const participationFee = parseFloat(formData.participationFee) || 0;
      const adjustments = parseFloat(formData.adjustments) || 0;
      return conciergeFee + participationFee + adjustments;
    } else {
      const cashPrice = parseFloat(formData.cashPrice) || 0;
      const docFee = parseFloat(formData.docFee) || 0;
      const titleFee = parseFloat(formData.titleFee) || 0;
      const tagFee = parseFloat(formData.tagFee) || 0;
      const conciergeFee = parseFloat(formData.conciergeFee) || 0;
      // Only include taxes if hasTax is true
      const salesTax = formData.hasTax ? (parseFloat(formData.salesTax) || 0) : 0;
      const surtax = formData.hasTax ? (parseFloat(formData.surtax) || 0) : 0;
      return cashPrice + docFee + titleFee + tagFee + conciergeFee + salesTax + surtax;
    }
  };

  const calculateTaxes = () => {
    if (!formData.hasTax) {
      setFormData(prev => ({ ...prev, salesTax: '', surtax: '' }));
      return;
    }

    const cashPrice = parseFloat(formData.cashPrice) || 0;
    const docFee = parseFloat(formData.docFee) || 0;
    const taxDeduction = parseFloat(formData.taxDeduction) || 0;
    const taxBase = Math.max(0, cashPrice + docFee - taxDeduction);
    const salesTax = Math.round(taxBase * 0.06 * 100) / 100;
    const surtax = Math.round(100 * (taxBase * 0.01 >= 50 ? 50 : taxBase * 0.01)) / 100;
    
    setFormData(prev => ({ 
      ...prev, 
      salesTax: salesTax.toFixed(2), 
      surtax: surtax.toFixed(2) 
    }));
  };

  React.useEffect(() => {
    if (type !== 'finance') {
      if (formData.hasTax) {
        calculateTaxes();
      } else {
        // Clear taxes when disabled
        setFormData(prev => ({ ...prev, salesTax: '', surtax: '' }));
      }
    }
  }, [formData.cashPrice, formData.docFee, formData.hasTax, formData.taxDeduction, type]);

  React.useEffect(() => {
    const total = calculateTotal();
    setFormData(prev => ({ ...prev, total: total.toFixed(2) }));
  }, [formData.cashPrice, formData.docFee, formData.titleFee, formData.tagFee, formData.conciergeFee, formData.salesTax, formData.surtax, formData.participationFee, formData.adjustments]);

  const validateForm = () => {
    const newErrors = {};
    
    if (type === 'finance') {
      if (!formData.conciergeFee) newErrors.conciergeFee = 'Concierge Fee is required';
      if (!formData.participationFee) newErrors.participationFee = 'Participation Fee is required';
    } else {
      if (!formData.cashPrice) newErrors.cashPrice = 'Cash Price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let invoiceId;
      
      if (editMode && invoiceData) {
        // Update existing invoice
        invoiceId = invoiceData.id;
      } else {
        // Create new invoice
        invoiceId = uuidv4();
      }
      
      // Format numeric values - convert to numbers, use 0 for blank values
      const formattedData = {};
      Object.keys(formData).forEach(key => {
        if (key === 'hasTax') {
          formattedData[key] = formData[key];
        } else if (key === 'taxDeduction') {
          formattedData[key] = parseFloat(formData[key]) || 0;
        } else {
          formattedData[key] = parseFloat(formData[key]) || 0;
        }
      });

      const revenue = formattedData.cashPrice + formattedData.docFee + formattedData.titleFee + formattedData.tagFee + formattedData.conciergeFee;
      const total = revenue + formattedData.salesTax + formattedData.surtax;

      // Format date as YYYY/MM/DD
      const now = new Date();
      const formattedDate = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}`;

      const invoiceDataToSave = {
        id: invoiceId,
        stock: stockNumber,
        type: type,
        date: formattedDate,
        revenue: revenue,
        total: total,
        ...formattedData
      };

      // Save/update invoice to invoices collection
      await firebase.firestore().collection('invoices').doc(invoiceId).set(invoiceDataToSave);

      // Update deal with invoice reference
      if (type === 'finance') {
        await firebase.firestore().doc('deals/' + stockNumber).set({ finance: invoiceId }, { merge: true });
      } else {
        await firebase.firestore().doc('deals/' + stockNumber).set({ invoice: invoiceId }, { merge: true });
      }

      // Call callback to update parent component
      onInvoiceAdded(invoiceDataToSave);
      
      // Close dialog
      onClose();
      
      // Reset form
      if (type === 'finance') {
        setFormData({
          conciergeFee: '',
          participationFee: '',
          adjustments: '',
          total: '',
          taxDeduction: tax_deduction
        });
      } else {
        setFormData({
          cashPrice: '',
          docFee: '',
          titleFee: '',
          tagFee: '',
          conciergeFee: '',
          downpayment: '',
          hasTax: true,
          salesTax: '',
          surtax: '',
          total: '',
          taxDeduction: tax_deduction
        });
      }
      setErrors({});
    } catch (error) {
      console.error('Error saving invoice:', error);
      // You could add error handling here
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    if (type === 'finance') {
      setFormData({
        conciergeFee: '',
        participationFee: '',
        adjustments: '',
        total: '',
        taxDeduction: tax_deduction
      });
    } else {
      setFormData({
        cashPrice: '',
        docFee: '',
        titleFee: '',
        tagFee: '',
        conciergeFee: '',
        downpayment: '',
        hasTax: true,
        salesTax: '',
        surtax: '',
        total: '',
        taxDeduction: tax_deduction
      });
    }
    setErrors({});
    onClose();
  };

  return (
    <StackedDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        sx={{
          backgroundColor: '#2c3e50',
          color: 'white',
          py: 3,
          px: 4,
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: 600,
          letterSpacing: '0.5px'
        }}
      >
        {editMode ? 'Edit Invoice' : (type === 'finance' ? 'Add Finance Invoice' : 'Add Sales Invoice')}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <CircularProgress size={20} sx={{ color: 'white' }} />
          </Box>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, pt: 2, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1
            }}
          >
            <CircularProgress size={40} sx={{ color: '#3498db' }} />
          </Box>
        )}
        <Grid container spacing={2}>
          {type === 'finance' ? (
            // Finance invoice fields
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                  Finance Details
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Concierge Fee"
                  type="number"
                  value={formData.conciergeFee}
                  onChange={(e) => handleInputChange('conciergeFee', e.target.value)}
                  error={!!errors.conciergeFee}
                  helperText={errors.conciergeFee}
                  inputProps={{ min: 0, step: 0.01 }}
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bank Participation Fee"
                  type="number"
                  value={formData.participationFee}
                  onChange={(e) => handleInputChange('participationFee', e.target.value)}
                  error={!!errors.participationFee}
                  helperText={errors.participationFee}
                  inputProps={{ min: 0, step: 0.01 }}
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Adjustments/Discounts"
                  type="number"
                  value={formData.adjustments}
                  onChange={(e) => handleInputChange('adjustments', e.target.value)}
                  inputProps={{ step: 0.01 }}
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
            </>
          ) : (
            // Regular sales invoice fields
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1, fontWeight: 500 }}>
                  Vehicle Pricing
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cash Price"
                  type="number"
                  value={formData.cashPrice}
                  onChange={(e) => handleInputChange('cashPrice', e.target.value)}
                  error={!!errors.cashPrice}
                  helperText={errors.cashPrice}
                  inputProps={{ min: 0, step: 0.01 }}
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Doc Fee"
                  type="number"
                  value={formData.docFee}
                  onChange={(e) => handleInputChange('docFee', e.target.value)}
                  // error={!!errors.docFee}
                  autoComplete="off"
                  helperText={errors.docFee}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1, fontWeight: 500 }}>
                  Additional Fees
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title Fee"
                  type="number"
                  value={formData.titleFee}
                  onChange={(e) => handleInputChange('titleFee', e.target.value)}
                  // error={!!errors.titleFee}
                  autoComplete="off"
                  helperText={errors.titleFee}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tag Fee"
                  type="number"
                  value={formData.tagFee}
                  onChange={(e) => handleInputChange('tagFee', e.target.value)}
                  // error={!!errors.tagFee}
                  helperText={errors.tagFee}
                  autoComplete="off"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Concierge Fee"
                  type="number"
                  value={formData.conciergeFee}
                  onChange={(e) => handleInputChange('conciergeFee', e.target.value)}
                  // error={!!errors.conciergeFee}
                  helperText={errors.conciergeFee}
                  autoComplete="off"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Down Payment"
                  type="number"
                  value={formData.downpayment}
                  onChange={(e) => handleInputChange('downpayment', e.target.value)}
                  autoComplete="off"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 0.5, fontWeight: 500 }}>
                  Tax Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Apply Taxes</InputLabel>
                  <Select
                    value={formData.hasTax}
                    onChange={(e) => handleInputChange('hasTax', e.target.value)}
                    label="Apply Taxes"
                    disabled={loading}
                    MenuProps={selectMenuProps}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Trade Tax Deduction"
                  type="number"
                  value={formData.taxDeduction}
                  onChange={(e) => handleInputChange('taxDeduction', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled
                  helperText={formData.hasTax ? `Tax base: $${Math.max(0, (parseFloat(formData.cashPrice) || 0) + (parseFloat(formData.docFee) || 0) - (parseFloat(formData.taxDeduction) || 0)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : "Trade-in value, discounts, etc."}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sales Tax"
                  type="number"
                  value={formData.salesTax}
                  disabled
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Surtax"
                  type="number"
                  value={formData.surtax}
                  disabled
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: 3,
                p: 2.5,
                mt: 1,
                border: '1px solid #e9ecef'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  textAlign: 'center',
                  fontWeight: 700,
                  color: '#495057',
                  letterSpacing: '0.5px'
                }}
              >
                Total: ${String(formData.total || '0.00').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            color: '#6c757d',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              color: '#495057'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 600,
            backgroundColor: '#3498db',
            boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)',
            '&:hover': {
              backgroundColor: '#2980b9',
              boxShadow: '0 6px 20px rgba(52, 152, 219, 0.4)'
            }
          }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : (editMode ? 'Update Invoice' : 'Save Invoice')}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
